'use client';

import React from 'react';
import Image from 'next/image';
import { motion, useReducedMotion } from 'framer-motion';

/**
 * HeroSection
 *
 * Renders the hero banner for the About page.
 * - Displays two animated background images side-by-side with gradient overlays for visual depth.
 * - Adds a dark overlay for text readability.
 * - Centers a large, responsive heading using Framer Motion for entrance animation.
 * - Respects user reduced motion preferences for accessibility.
 *
 * Layout adapts for all screen sizes, ensuring the hero remains visually impactful and readable.
 */
const HeroSection: React.FC = () => {
  // Detects if user prefers reduced motion for accessibility
  const reduce = useReducedMotion();

  return (
    <section className="relative w-full h-[400px] sm:h-[500px] md:h-[600px] lg:h-[800px] overflow-hidden">
      {/* Background Images: Two animated halves with gradients */}
      <div className="absolute inset-0 flex">
        <motion.div
          className="w-1/2 h-full relative"
          initial={reduce ? undefined : 'hidden'}
          whileInView={reduce ? undefined : 'visible'}
          viewport={{ once: true, amount: 0.2 }}
          variants={{
            hidden: { opacity: 0, x: -30 },
            visible: { opacity: 1, x: 0, transition: { duration: 0.8 } },
          }}
        >
          <Image
            src="/images/about/andoria-ai-about-page-header.jpg"
            alt="Modern office space"
            fill
            sizes="(max-width: 640px) 100vw, 50vw"
            className="object-cover"
            priority
          />
          {/* Gradient overlays for left image */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/30" />
          <div className="absolute inset-0 bg-blue-gradient-start/20" />
        </motion.div>

        <motion.div
          className="w-1/2 h-full relative"
          initial={reduce ? undefined : 'hidden'}
          whileInView={reduce ? undefined : 'visible'}
          viewport={{ once: true, amount: 0.2 }}
          variants={{
            hidden: { opacity: 0, x: 30 },
            visible: { opacity: 1, x: 0, transition: { duration: 0.8 } },
          }}
        >
          <Image
            src="/images/about/andoria-ai-about-page-header2.jpg"
            alt="Technology workspace"
            fill
            sizes="(max-width: 640px) 100vw, 50vw"
            className="object-cover"
            priority
          />
          {/* Gradient overlays for right image */}
          <div className="absolute inset-0 bg-gradient-to-l from-transparent to-black/30" />
          <div className="absolute inset-0 bg-blue-gradient-start/20" />
        </motion.div>
      </div>

      {/* Overlay: Adds a dark layer for text readability */}
      <div className="absolute inset-0 bg-black/40" />

      {/* Content: Centered heading with motion animation */}
      <div className="relative z-10 flex items-center justify-center h-full px-4">
        <motion.h1
          initial={reduce ? undefined : { opacity: 0, y: 20 }}
          whileInView={reduce ? undefined : { opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, delay: 0.08, ease: 'easeOut' }}
          className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-semibold text-white text-center font-manrope tracking-[-0.5px] sm:tracking-[-1px]"
        >
          About Andoria
        </motion.h1>
      </div>
    </section>
  );
};

export default HeroSection;
